/**
 * @voltxampere/web — Polish effect priority + LOD (Sprint 58b)
 *
 * Adaptive degradation framework. The canvas fx loop samples its
 * own dt → FPS estimate (rolling median, 30-frame window). When
 * the FPS drops below tier thresholds, the framework progressively
 * disables polish effects starting with the cheapest-to-cut (solder
 * shimmer) and finishing with vibration (the most expensive on
 * dense fixtures).
 *
 * `glow` (snap glow) and `hover` (hover tooltip) are *critical UX*
 * and never auto-disable. The user can still flip the manual
 * override to `off` to skip them.
 *
 * Manual override (Settings → polish.level):
 *   - "full"     — all effects on (default)
 *   - "reduced"  — shimmer + vibration off
 *   - "minimal"  — only glow + hover stay
 *   - "off"      — every polish effect off
 *
 * Hysteresis: a tier engages on the down-stroke at the threshold,
 * but only re-enables when FPS climbs 5 FPS above it. This avoids
 * flapping between tiers when FPS sits near a boundary.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { computed, signal, type ReadonlySignal } from "@preact/signals-core";

export type PolishLevel = "full" | "reduced" | "minimal" | "off";

export type PolishEffectId =
  | "vibration"
  | "connection-animation"
  | "shimmer"
  | "snap-glow"
  | "hover-tooltip";

/** FPS rolling-median sample window. */
export const FPS_SAMPLE_COUNT = 30;
/** Auto-degrade thresholds — engage. */
export const FPS_DROP_SHIMMER = 50;
export const FPS_DROP_CONNECTION = 40;
export const FPS_DROP_VIBRATION = 30;
/** Hysteresis margin — release threshold = engage + margin. */
export const FPS_RECOVER_MARGIN = 5;

/** User-selected polish level. Persisted by the settings pane. */
export const polishLevel = signal<PolishLevel>("full");

/** Live FPS estimate (rolling median, ~30-frame window). */
export const measuredFps = signal<number | null>(null);

/** Snapshot of currently auto-disabled effects (engagement state). */
export const autoDisabledEffects = signal<readonly PolishEffectId[]>([]);

const fpsSamples: number[] = [];

/**
 * Push a per-frame dt measurement (ms). Updates rolling median +
 * triggers tier engagement / release.
 */
export function recordFrame(dtMs: number): void {
  if (dtMs <= 0 || !Number.isFinite(dtMs)) return;
  const fps = 1000 / dtMs;
  fpsSamples.push(fps);
  if (fpsSamples.length > FPS_SAMPLE_COUNT) fpsSamples.shift();
  if (fpsSamples.length < FPS_SAMPLE_COUNT / 2) return;
  // Median of the window — robust to single-frame spikes.
  const sorted = [...fpsSamples].sort((a, b) => a - b);
  const mid = sorted[Math.floor(sorted.length / 2)] ?? fps;
  measuredFps.value = mid;
  applyTierTransitions(mid);
}

function applyTierTransitions(fps: number): void {
  const disabled = new Set(autoDisabledEffects.value);
  // Engage / release per tier with hysteresis.
  toggleTier(fps, FPS_DROP_SHIMMER, "shimmer", disabled);
  toggleTier(fps, FPS_DROP_CONNECTION, "connection-animation", disabled);
  toggleTier(fps, FPS_DROP_VIBRATION, "vibration", disabled);
  // Project the order so the snapshot is stable.
  const ordered: PolishEffectId[] = [];
  for (const id of ["vibration", "connection-animation", "shimmer"] as const) {
    if (disabled.has(id)) ordered.push(id);
  }
  // Only mutate the signal when the set actually changed.
  const current = autoDisabledEffects.value;
  if (ordered.length !== current.length || ordered.some((id, i) => current[i] !== id)) {
    autoDisabledEffects.value = ordered;
  }
}

function toggleTier(
  fps: number,
  engageFps: number,
  effect: PolishEffectId,
  disabled: Set<PolishEffectId>,
): void {
  if (disabled.has(effect)) {
    if (fps > engageFps + FPS_RECOVER_MARGIN) disabled.delete(effect);
  } else {
    if (fps < engageFps) disabled.add(effect);
  }
}

/**
 * Derived: per-effect "should render?" booleans. Renderer reads
 * these instead of inferring its own gates. Order of precedence:
 *
 *   1. Manual override (level) — wins absolutely.
 *   2. Auto-degrade — engages within the manual-allowed set.
 *   3. Otherwise — effect renders.
 */
export const effectEnabled: ReadonlySignal<Readonly<Record<PolishEffectId, boolean>>> = computed(
  () => {
    const level = polishLevel.value;
    const auto = new Set(autoDisabledEffects.value);

    const allowed = manualAllowedByLevel(level);
    const result: Record<PolishEffectId, boolean> = {
      vibration: false,
      "connection-animation": false,
      shimmer: false,
      "snap-glow": false,
      "hover-tooltip": false,
    };
    for (const id of Object.keys(result) as readonly PolishEffectId[]) {
      if (!allowed.has(id)) continue;
      if (auto.has(id)) continue;
      result[id] = true;
    }
    return result;
  },
);

function manualAllowedByLevel(level: PolishLevel): ReadonlySet<PolishEffectId> {
  switch (level) {
    case "full":
      return new Set<PolishEffectId>([
        "vibration",
        "connection-animation",
        "shimmer",
        "snap-glow",
        "hover-tooltip",
      ]);
    case "reduced":
      return new Set<PolishEffectId>(["connection-animation", "snap-glow", "hover-tooltip"]);
    case "minimal":
      return new Set<PolishEffectId>(["snap-glow", "hover-tooltip"]);
    case "off":
      return new Set<PolishEffectId>([]);
  }
}

export function setPolishLevel(level: PolishLevel): void {
  polishLevel.value = level;
}

/** Test helper — clears FPS history + resets every signal. */
export function resetPolishPriorityForTests(): void {
  fpsSamples.length = 0;
  measuredFps.value = null;
  autoDisabledEffects.value = [];
  polishLevel.value = "full";
}
